// ── Retail pricing ──────────────────────────────────────────────────────────
//
// RRP derivation and instalment plans. These lived twice - identically, for now
// - in QuotePage and ComparePage. Duplicated pricing is the kind that diverges
// quietly and then shows up as two different numbers quoted to the same
// customer from two screens.

/**
 * Round up to a price ending in 9.
 *
 * Rounds to the nearest ten first, then drops a penny/cent: 104 -> 99,
 * 105 -> 109. The halfway case follows Math.round, which rounds .5 upward.
 */
export function roundToNearest9(val: number): number {
  return Math.round(val / 10) * 10 - 1;
}

/** Cost-to-retail multiplier: 25% margin, then 18% VAT. */
export const RRP_MARKUP = 1.25;
export const RRP_VAT = 1.18;

/**
 * The price to show for a build.
 *
 * A stored RRP always wins - it is a deliberate commercial decision. Only when
 * one is absent (or zero, which the column uses for "unset") is a price derived
 * from cost.
 */
export function calcRrp(rrp: number | null | undefined, totalCost: number): number {
  if (rrp != null && rrp > 0) return rrp;
  return roundToNearest9(totalCost * RRP_MARKUP * RRP_VAT);
}

/** Instalment plans offered against a build's RRP. */
export const EPS_PLANS = [
  { months: 12, markup: 0.18 },
  { months: 24, markup: 0.25 },
  { months: 36, markup: 0.35 },
] as const;

export type EpsPlan = (typeof EPS_PLANS)[number];

/**
 * Monthly instalment for a plan.
 *
 * Rounded UP to the cent, so the instalments never total less than the marked-up
 * price - the rounding difference lands in the customer's final payment rather
 * than being absorbed.
 */
export function calcEps(rrp: number, markup: number, months: number): number {
  return Math.ceil(((rrp * (1 + markup)) / months) * 100) / 100;
}
